import { Button, Skeleton } from '@design-system';
import Link from 'next/link';
import { FC } from 'react';

import { useProducts } from '../../hooks/useProducts';
import { ProductIdStoryblok } from '../../types/generated-storyblok';
import { renderShopifyArtistName } from '../../utils/artists';
import { productUrl } from '../../utils/productUrl';
import { shopifyAdminIdToGraphqlId } from '../../utils/shopifyIds';
import { Image } from '../Image';
import ProductTileBadge from '../ProductTile/ProductTileBadge';
import ProductTileStatus from '../ProductTile/ProductTileStatus';

interface Props {
  blok: ProductIdStoryblok;
  variant?: 'default' | 'purchase';
  target?: '_self' | '_blank';
}

export const ArticleProduct: FC<Props> = ({
  blok,
  target = '_self',
  variant = 'default',
}) => {
  const { loading, products } = useProducts([
    shopifyAdminIdToGraphqlId(blok.id!),
  ]);
  const [product] = products;

  if (loading || !product) {
    return (
      <div className="flex w-full items-center gap-3 pt-3 first:pt-0 md:pt-4">
        <Skeleton className="h-16 w-14 shrink-0" />
        <div className="flex w-full flex-col gap-1">
          <Skeleton className="h-9 w-3/4 md:h-5" />
          <Skeleton className="h-9 w-1/2 md:h-5" />
        </div>
      </div>
    );
  }

  return (
    <Link
      key={product.id}
      className="group flex gap-3 pt-3 first:pt-0 md:pt-4"
      href={productUrl(product.full_slug || '', product.productType)}
      prefetch={false}
      target={target}
    >
      <div className="w-16 shrink-0">
        <Image
          alt={product.alt || product.title}
          hoverEffect
          shape="rounded"
          src={product.image}
          width={64}
        />
      </div>
      <div className="flex w-full flex-col overflow-hidden py-px">
        <p className="w-full font-bold transition duration-300 ease-out group-hover:text-neutral-4">
          {renderShopifyArtistName(product.artist)}
        </p>
        <p className="w-full transition duration-300 ease-out group-hover:text-neutral-4">
          {product.title}
        </p>
        <ProductTileStatus product={product} />
      </div>
      <div className="flex shrink-0 items-center justify-center">
        {variant === 'purchase' ? (
          <Button
            disabled={product.availability !== 'Available'}
            label="Buy"
            size="md"
            variant="primary"
          />
        ) : (
          <ProductTileBadge product={product} />
        )}
      </div>
    </Link>
  );
};
